/**
 * Store pour la gestion des secteurs d'activité
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useActivitySectorStore = defineStore('activitySector', (() => {

    // State de la liste complète avec les descendants et les métiers
    const tree = ref(null);
    const sectorsFromTree = ref([]);

    // State de la liste des secteurs
    const sectorList = ref({});

    // State pour les différencier les données de la liste des secteurs
    const sectors = ref([]);

    /**
     * Retourne l'arborescence
     * @return array
     */
    const getTree = computed(() => tree.value);

    /**
     * Retourne les secteurs d'activité
     * @return array
     */
    const getSectorList = computed(() => sectorList.value || {});

    /**
     * Retourne le secteur d'activité dont l'identifiant est en paramètre
     * @param int sectorId
     * @return object|null
     */
    const getSectorById = computed(() => {
        return ((sectorId) => {
            const index = sectors.value.findIndex((sectorFromState) => sectorFromState.id === sectorId);
            if(index === -1) {
                return null;
            }
            return sectors.value[index];
        });
    });

    /**
     * Chargement de l'arborescence
     * @return Promise
     */
    const loadTree = (() => {

        if(getTree.value !== null) {
            return Promise.resolve();
        }

        const path = '/api/activity-sectors/tree';

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.sectors) {

                // Ajoute les secteurs à la liste complète des secteurs
                sectorsFromTree.value = [];
                response.data.sectors.forEach((sector) => {
                    sectorsFromTree.value.push(sector);
                    sector.children.forEach((child) => {
                        sectorsFromTree.value.push(sector);
                    });
                });

                tree.value = response.data.sectors;
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });
    });

    /**
     * Chargement des secteurs d'activité
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadSectorList = ((params) => {

        const path = '/api/admin/activity-sectors';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetSectorList();
                }

                updateSectorList(response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Chargement des données d'un secteur d'activité
     * @param int sectorId
     * @return Promise
     */
    const loadSectorById = ((sectorId) => {

        const existingSector = getSectorById.value(sectorId);
        if(existingSector !== null) {
            return Promise.resolve();
        }

        const path = '/api/admin/activity-sectors/:sectorId'.replace(':sectorId', sectorId);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.sector) {
                updateSector(response.data.sector);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Ajoute une proposition de secteur
     * @param string proposal
     * @return Promise
     */
    const addProposal = ((proposal) => {

        const path = '/api/activity-sectors/proposal';

        return axios.post(path, {
            name: proposal
        }).then((response) => {
            if(response && response.data && response.data.success) {
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Ajoute un secteur
     * @param object sector Données du secteur à ajouter
     * @return void
     */
    const addSector = ((sector) => {

        if(sectorList.value.total === undefined) {
            sectorList.value = {
                total: 0,
                sectors: []
            };
        }

        sectorList.value.total += 1;
        sectorList.value.sectors.push(sector);

        sectors.value.push(sector);

    });

    /**
     * Modifie un secteur
     * @param object sector Données du secteur à mettre à jour dans la liste
     * @return Promise
     */
    const updateSector = ((sector) => {

        const currentSectors = sectors.value;

        const index = currentSectors.findIndex((sectorFromState) => sectorFromState.id === sector.id);
        if(index !== -1) {
            sectors.value[index] = sector;
        } else {
            sectors.value.push(sector);
        }

    });

    /**
     * Modifie les secteurs
     * @param data Données des secteurs : total et items
     * @return void
     */
    const updateSectorList = ((data) => {

        const currentData = sectorList.value;
        if(currentData.total === undefined) {
            sectorList.value = {
                total: data.total,
                sectors: data.items
            };

            data.items.forEach((sector) => {
                updateSector(sector);
            });

        } else {
            sectorList.value.total = data.total;

            const currentSectors = sectorList.value.sectors;
            const newSectors = data.items.filter((item) => ! currentSectors.find((current) => current.id === item.id));
            sectorList.value.sectors = [ ...currentSectors, ...newSectors ];

            newSectors.forEach((sector) => {
                updateSector(sector);
            });
        }

    });

    /**
     * Réinitialisation de la liste des secteurs
     * @return void
     */
    const resetSectorList = (() => {
        sectorList.value = {};
    });


    return {
        getSectorList,
        loadSectorList,
        resetSectorList,
        addSector,
        getSectorById,
        loadSectorById,
        getTree,
        loadTree,
        addProposal
    };
}));
